import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler, FieldValues } from 'react-hook-form';
import { BillingAddress, Partner } from '@marageti/z4-sdk/lib/travel/Partner';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { enqueueSnackbar } from 'notistack';
import { ContainerProps } from './types';
import EditBillingInfoFormView from './edit-billing-info-form-view';

const EditBillingInfoFormContainer = ({
  open,
  onClose,
}: ContainerProps) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    reset,
  } = useForm({
    mode: 'onChange',
  });

  const { partnerId } = useParams();
  const navigate = useNavigate();
  const apiClient = useApiClient();

  const [submissionError, setSubmissionError] = useState<ApiError>();

  const { data: partner, mutate } = useFetch<Partner>(apiClient.partnersClient.getPartner, [partnerId], true);

  useEffect(() => {
    if (partner?.billingAddress) {
      reset({
        name: partner.billingAddress.name || '',
        emails: partner.billingAddress.emails || [],
        address: partner.billingAddress.street || '',
        city: partner.billingAddress.city || '',
        state: partner.billingAddress.state || '',
        postalCode: partner.billingAddress.postalCode || '',
        country: partner.billingAddress.country || null,
      });
    }
  }, [partner, reset]);

  const handleCancel = () => {
    reset();
    onClose();
  };

  const submitHandler: SubmitHandler<FieldValues> = async (data) => {
    setSubmissionError(undefined);
    try {
      if (!partner) return;
      const updatedBillingInfo: BillingAddress = {
        ...partner.billingAddress,
        name: data.name,
        emails: data.emails,
        street: data.address,
        city: data.city,
        state: data.state,
        postalCode: data.postalCode,
        country: data.country,
      };
      await apiClient.partnersClient.putPartnerBillingAddress(partner.id!, updatedBillingInfo);
      mutate();
      enqueueSnackbar('Billing info saved.', { variant: 'success' });
      onClose();
    } catch (e: any) {
      if (e.code === 401) navigate('/logout');
      if (e !== undefined) enqueueSnackbar('Something went wrong on our end. Please try again.', { variant: 'default' });
      setSubmissionError(e);
    }
  };

  return (
    <EditBillingInfoFormView
      control={control}
      errors={errors}
      isValid={isValid}
      isSubmitting={isSubmitting}
      handleSubmit={handleSubmit}
      onCancel={handleCancel}
      submitHandler={submitHandler}
      open={open}
      register={register}
      submissionError={submissionError}
      partner={partner}
    />
  );
};

export default EditBillingInfoFormContainer;
